import { computed, inject, Injectable, Signal, signal, WritableSignal } from "@angular/core";
import { ActivatedRouteSnapshot, Router } from "@angular/router";

import { MessageService } from "primeng/api";

import { AppStorage } from "@bbwt/utils/app-storage";
import { Message } from "@bbwt/classes";

import { FormIODefinitionService } from "@features/bb-formio/services/formio-definition.service";

import { MultiUserFormAssociationLink } from "@main/formio/multi-user-form/dto/multi-user-form-association-links.dto";
import { MultiUserFormService } from "@main/formio/multi-user-form/services/multi-user-form.service";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { UserService } from "@main/users";

import { IEpisode } from "@project/models/episodes/episode";
import { FormIODefinition } from "@features/bb-formio";

import { MultiUserFormAssociation, NewMultiUserFormAssociation } from "../dto/multi-user-form-associations.dto";
import { MultiUserFormAssociationsService } from "./multi-user-form-associations.service";
import { MultiUserFormAccessService } from "./multi-user-form-access.service";
import { MultiUserFormDef } from "../dto/multi-user-form.dto";


@Injectable()
export class MultiUserFormSetupService {
    public static readonly FormIdQueryParamName: string = "formId";
    public static readonly TargetUserQueryParamName: string = "target";
    public static readonly ReturnUrlQueryParamName: string = "returnUrl";
    public static readonly FormDataIdQueryParamName: string = "formDataId";
    public static readonly MufAssociationIdQueryParamName: string = "mufId";

    private multiUserFormAssociationService: MultiUserFormAssociationsService = inject(MultiUserFormAssociationsService);
    private multiUserFormAccessService: MultiUserFormAccessService = inject(MultiUserFormAccessService);
    private formDefinitionService: FormIODefinitionService = inject(FormIODefinitionService);
    private multiUserFormService: MultiUserFormService = inject(MultiUserFormService);
    private patientService: PatientService = inject(PatientService);
    private messageService: MessageService = inject(MessageService);
    private userService: UserService = inject(UserService);
    private router: Router = inject(Router);

    protected _mufAssociation: WritableSignal<MultiUserFormAssociation> = signal<MultiUserFormAssociation>(null);
    protected _formDefinition: WritableSignal<FormIODefinition> = signal<FormIODefinition>(null);
    protected _mufDefinition: WritableSignal<MultiUserFormDef> = signal<MultiUserFormDef>(null);
    protected _episodeId: WritableSignal<string> = signal<string>(null);
    protected _patientId: WritableSignal<string> = signal<string>(null);
    protected _visible: WritableSignal<boolean> = signal<boolean>(null);

    public mufAssociation: Signal<MultiUserFormAssociation> = this._mufAssociation.asReadonly();
    public formDefinition: Signal<FormIODefinition> = this._formDefinition.asReadonly();
    public mufDefinition: Signal<MultiUserFormDef> = this._mufDefinition.asReadonly();
    public patientId: Signal<string> = this._patientId.asReadonly();
    public episodeId: Signal<string> = this._episodeId.asReadonly();
    public visible: Signal<boolean> = this._visible.asReadonly();

    public mufAssociationFirstStepLink: Signal<MultiUserFormAssociationLink> = this.computeMufAssociationFirstStepLink();
    public isSelfTargetStage: Signal<boolean> = this.computeSelfTargetStage();


    private computeMufAssociationFirstStepLink(): Signal<MultiUserFormAssociationLink> {
        return computed(
            (): MultiUserFormAssociationLink => this.mufAssociation()?.multiUserFormAssociationLinks
                ?.find(link => link.multiUserFormStage.sequenceStepIndex == 1)
        );
    }

    private computeSelfTargetStage(): Signal<boolean> {
        return computed(
            (): boolean => this.mufAssociationFirstStepLink()?.userId == this.userService.currentUser.id
        );
    }


    public async saveMufInstance(newMufAssociation: NewMultiUserFormAssociation): Promise<void> {
        this._visible.set(false);

        const mufAssociation: MultiUserFormAssociation = await this.createMufAssociation(newMufAssociation);
        this._mufAssociation.set(mufAssociation);

        if (!this.validateMufAssociation()) return this.cancelMufInstance();

        if (!this.isSelfTargetStage()) return this.cancelMufInstance();

        this.redirectToMufInstance();

        this.cancelMufInstance();
    }

    public async triggerMufInstanceSetup(route: ActivatedRouteSnapshot, attemptedUrl?: string): Promise<void> {
        const formId: string = route.queryParams[MultiUserFormSetupService.FormIdQueryParamName];

        // Navigate to home page only when necessary, i.e., when browser url was replaced manually.
        // If this happens, the app redirects temporarily to the root url ("\") and, if the guard
        // returns false there the page remains in the root, leaving the user in a blank page.
        if (this.router.url === "/") void this.router.navigate(["/app"]);

        await this.createMufInstance(formId, attemptedUrl);
    }

    public async createMufInstance(id: string, attemptedUrl?: string): Promise<void> {
        // Clean service memory from possible old usages of this method.
        this.cancelMufInstance();

        const hasAccess: boolean = this.multiUserFormAccessService.hasBasicAccess();
        if (!hasAccess) return;

        if (!this.patientService.currentPatientId) {
            // When triggered from a URL (attemptedUrl is provided), store the URL and prompt
            // for patient selection. After the user selects a patient, the patient-switch
            // component will redirect back to this URL, re-triggering the MUF setup flow.
            if (attemptedUrl) {
                AppStorage.setItem("attemptedUrl", attemptedUrl);
                this.patientService.triggerRequiredPatientSelection();
                return;
            }
        }

        const patientId: string = this.patientService.currentPatientId;
        this._patientId.set(patientId);

        if (!this.validatePatient()) return;

        const formDefinition: FormIODefinition = await this.formDefinitionService.get(id);
        this._formDefinition.set(formDefinition);

        if (!this.validateFormDefinition()) return;

        const mufDefinition: MultiUserFormDef = await this.getMufDefinition(this.formDefinition());
        this._mufDefinition.set(mufDefinition);

        if (!this.validateMufDefinition()) return;

        const episode: IEpisode = await this.getValidEpisode(this._formDefinition()?.episode);
        this._episodeId.set(episode?.id);

        if (this.multiUserFormAccessService.hasAccess(formDefinition)) {
            // Show the dialog only if this user has access to the form.
            this._visible.set(true);

            // Otherwise, clean the service memory.
        } else this.cancelMufInstance();
    }

    public cancelMufInstance(): void {
        this._mufAssociation.set(null);
        this._formDefinition.set(null);
        this._mufDefinition.set(null);
        this._patientId.set(null);
        this._episodeId.set(null);
        this._visible.set(false);
    }


    protected async createMufAssociation(newMufAssociation: NewMultiUserFormAssociation): Promise<MultiUserFormAssociation> {
        if (!newMufAssociation) return null;

        return await this.multiUserFormAssociationService.addNewMUFAssociation(newMufAssociation);
    }

    protected async getMufDefinition(formDefinition: FormIODefinition): Promise<MultiUserFormDef> {
        if (!formDefinition?.activeRevisionId) return null;

        return await this.multiUserFormService.getByFormRevisionId(formDefinition.activeRevisionId);
    }

    protected async getValidEpisode(episode: IEpisode): Promise<IEpisode> {
        if (!episode) return null;

        return this.formDefinitionService.ensureInstanceEpisodeExistsForPatient(episode, this.patientId());
    }

    protected redirectToMufInstance(): void {
        if (!this.mufAssociation()) return;

        const queryParams: Record<string, any> = {}

        queryParams[MultiUserFormSetupService.FormIdQueryParamName] = this.formDefinition()?.id;
        queryParams[MultiUserFormSetupService.MufAssociationIdQueryParamName] = this.mufAssociation()?.id;
        queryParams[MultiUserFormSetupService.FormDataIdQueryParamName] = this.mufAssociation()?.formDataId;
        queryParams[MultiUserFormSetupService.TargetUserQueryParamName] = this.userService.currentUser.id;
        queryParams[MultiUserFormSetupService.ReturnUrlQueryParamName] = this.router.url;

        void this.router.navigate(["app/formio/multiuser/display"], { queryParams });
    }


    public validateMufUrl(route: ActivatedRouteSnapshot): boolean {
        const mufId: string = route.queryParams[MultiUserFormSetupService.MufAssociationIdQueryParamName];
        const formDataId: string = route.queryParams[MultiUserFormSetupService.FormDataIdQueryParamName];
        const formId: string = route.queryParams[MultiUserFormSetupService.FormIdQueryParamName];

        // If muf association and form data id are present, the url is valid.
        if (!!formDataId?.length && !!mufId?.length) return true;

        // If they are not, but form id is present, return false to trigger muf instance setup.
        if (!!formId?.length) return false;

        // Nothing to do without form id, notify the user about the problem.
        const message: string = "There is no valid form ID in the given url";
        const summary: string = "Form ID missing";

        this.messageService.add(Message.Warning(message, summary));
    }

    protected validateFormDefinition(): boolean {
        if (!!this.formDefinition()?.activeRevisionId) return true;

        const message: string = !this.formDefinition
            ? "The specified form id points to a non-existent form"
            : "The specified form id points to an invalid form that has no active revision";

        const summary: string = !this.formDefinition
            ? "Form not found"
            : "Invalid form";

        this.messageService.add(Message.Error(message, summary));

        return false;
    }

    protected validateMufAssociation(): boolean {
        if (!!this.mufAssociation()) return true;

        const message: string = "Couldn't create corresponding multi-user form instance";
        const summary: string = "Error creating multi-user form";

        this.messageService.add(Message.Error(message, summary));

        return false;
    }

    protected validateMufDefinition(): boolean {
        if (!!this.mufDefinition()?.setupReady) return true;

        const message: string = !this.mufDefinition
            ? "The specified form id points to a non-existent multi-user form"
            : "The multi-user form associated to this id has some stages that needs setup";

        const summary: string = !this.mufDefinition
            ? "Multi-user form not found"
            : "Multi-user form not ready";

        this.messageService.add(Message.Error(message, summary));

        return false;
    }

    protected validatePatient(): boolean {
        if (!!this.patientId()) return true;

        const message: string = "You must select a patient to instance a multi-user form";
        const summary: string = "Patient not selected";

        this.messageService.add(Message.Warning(message, summary));

        return false;
    }
}
